import { Promise } from 'es6-promise';
import { FetchPromise } from "./FetchPromise";
import { Container } from '../Containers/Container';
import { IntraActiveTokenService } from '../IntraActiveTokenService';

const SESSION_STORAGE_REAUTH_ID: string = "lastReauthenticationAttemp";

export type onKnownHttpErrorMethodType = ((errorText: string, response: Response) => void);

export interface IFetchOptions {
  container?: Container;
  onReAuthentication?: ((value: Response) => void);
  onKnownError?: onKnownHttpErrorMethodType;
  isRequestRetried?: boolean;
}

export class Fetch {
  private readonly promise: Promise<Response>;
  private wellKnownHttpError: () => Promise<any>;
  public constructor(input: RequestInfo, init?: RequestInit, public readonly options?: IFetchOptions) {
    this.promise = new Promise<Response>((resolve, reject) => {
      fetch(input, init)
        .then((response) => {
          if (!response.ok) {
            if (response.status === 401) {
              if (this.options.onReAuthentication) {
                this.options.onReAuthentication(response);
              }
              try {
                if (!this.hasTriedToReauthenticate(input)) {
                  this.setLastReuthenticatioAttemptFlag(input);

                  const intraActiveTokenService = options.container.resolve(IntraActiveTokenService);
                  intraActiveTokenService.logout();
                  intraActiveTokenService.getToken()
                    .then(() => {
                      if (!options.isRequestRetried) {
                        new Fetch(input, init, {
                          isRequestRetried: true,
                          container: options.container,
                          onKnownError: options.onKnownError,
                          onReAuthentication: options.onReAuthentication,
                        })
                        .then(() => resolve(response))
                        .catch(() => this.invokeErrorLogic(response, options, reject));
                      } else {
                        this.invokeErrorLogic(response, options, reject);
                      }
                    })
                    .catch(() => this.invokeErrorLogic(response, options, reject));
                    return;
                }
              } catch(e) {
                reject(response);
              }
            }

            return this.invokeErrorLogic(response, options, reject);
          }
          
          resolve(response);
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  public then<TResult1 = Response, TResult2 = never>(onfulfilled?: ((value: Response) => TResult1 | PromiseLike<TResult1>) | undefined | null, onrejected?: ((reason: any) => TResult2 | PromiseLike<TResult2>) | undefined | null): FetchPromise<TResult1> {
    const newPromise = this.promise.then(onfulfilled, onrejected);
    return new FetchPromise<TResult1>(newPromise, () => this.wellKnownHttpError);
  }
  public catch<TResult = never>(onrejected?: ((reason: any) => TResult | PromiseLike<TResult>) | undefined | null): FetchPromise<TResult> {
    const newPromise = this.promise.catch((reason) => {
      if (this.wellKnownHttpError) {
        return this.wellKnownHttpError();
      } else {
        return onrejected(reason);
      }
    });
    return new FetchPromise<TResult>(newPromise, () => this.wellKnownHttpError);
  }

  private setLastReuthenticatioAttemptFlag(requestInfo: RequestInfo): void {
    const uri: string = typeof(requestInfo) === "string"
      ? requestInfo
      : (requestInfo as Request).url;

    sessionStorage.setItem(`${SESSION_STORAGE_REAUTH_ID}:${uri}`, JSON.stringify(new Date()));
  }

  private hasTriedToReauthenticate(requestInfo: RequestInfo): boolean {
    const uri: string = typeof(requestInfo) === "string"
      ? requestInfo
      : (requestInfo as Request).url;

    const sessionLastAuthenticationString = sessionStorage.getItem(`${SESSION_STORAGE_REAUTH_ID}:${uri}`);
    if (sessionLastAuthenticationString) {
      const lastAuthenticationAttemp: Date = new Date(JSON.parse(sessionLastAuthenticationString));
      const tenMinutesAgo = new Date(new Date().setMinutes(new Date().getMinutes() - 10));
      if (tenMinutesAgo < lastAuthenticationAttemp) {
        return true;
      }
    }

    return false;
  }

  private tryParseJson(text: string): any {
    try {
      return JSON.parse(text);
    } catch(e) {
      return null;
    }
  }
  
  private invokeErrorLogic(response: Response, options: IFetchOptions, reject: (error?: any) => void): void | PromiseLike<void> {
    return response.text().then((jsonResponseAsText: string) => {
      try {
        if (options.onKnownError) {
          const jsonResponse = this.tryParseJson(jsonResponseAsText);
          const message = jsonResponse
            ? (jsonResponse.message !== undefined
              ? jsonResponse.message
              : jsonResponseAsText)
            : jsonResponseAsText;
          this.wellKnownHttpError = () => {
            options.onKnownError(message, response);
            return Promise.resolve();
          };
        }
        // The rejection will check for wellKnownHttpError and skip the normal 'reject' method callback
        reject(response);
      } catch (e) {
        reject(response);
      }
    });
  }
}



// WEBPACK FOOTER //
// ./src/Fetch/Fetch.ts